import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

import { resolveIanaTimeZone } from "../../utils/timeZoneUtils"

import { normalizeBookableEventDurationUnit } from "./bookableEventType.utils"

dayjs.extend(utc)
dayjs.extend(timezone)

export type BookableOccurrencePayload = {
  id?: number | string
  startsAt?: string
  starts_at?: string
  endsAt?: string
  ends_at?: string
  timeZone?: string
  time_zone?: string
  allDay?: boolean
  all_day?: boolean
}

type PracticeEventTypes = Array<{
  id?: number | string
  slug?: string
  nextUpcomingOccurrence?: BookableOccurrencePayload | null
  next_upcoming_occurrence?: BookableOccurrencePayload | null
}>

type EventCardItem = {
  id?: number | string
  slug?: string
  itemType?: string
  bookableEventTypeId?: number | string
  bookable_event_type_id?: number | string
  duration?: number | null
  durationUnit?: unknown
  duration_unit?: unknown
}

const occurrenceFromType = (eventType: PracticeEventTypes[number] | undefined): BookableOccurrencePayload | null => {
  if (!eventType) return null
  return eventType.nextUpcomingOccurrence ?? eventType.next_upcoming_occurrence ?? null
}

export const resolveBookableEventTypeId = (item: EventCardItem): number | string | undefined => {
  const explicit = item.bookableEventTypeId ?? item.bookable_event_type_id
  if (explicit != null) return explicit
  if (item.itemType === "event") return item.id
  return undefined
}

/** Next upcoming occurrence for a group-event card (from preloaded practice.bookableEventTypes). */
export const nextUpcomingOccurrenceForEventCard = (
  item: EventCardItem,
  practice?: { bookableEventTypes?: PracticeEventTypes }
): BookableOccurrencePayload | null => {
  const types = practice?.bookableEventTypes ?? []
  const typeId = resolveBookableEventTypeId(item)

  if (typeId != null) {
    const byId = types.find((t) => String(t.id) === String(typeId))
    const fromId = occurrenceFromType(byId)
    if (fromId) return fromId
  }

  if (item.slug) {
    const bySlug = types.find((t) => t.slug === item.slug)
    const fromSlug = occurrenceFromType(bySlug)
    if (fromSlug) return fromSlug
  }

  return null
}

export const formatBookableEventOccurrenceCardDate = (
  occurrence: BookableOccurrencePayload,
  item?: EventCardItem
): string => {
  const startsAt = occurrence.startsAt ?? occurrence.starts_at
  if (!startsAt) return ""

  const tz = resolveIanaTimeZone(occurrence.timeZone ?? occurrence.time_zone)
  const allDay = occurrence.allDay ?? occurrence.all_day
  const rawUnit = item?.durationUnit ?? item?.duration_unit
  const durationUnit = normalizeBookableEventDurationUnit(rawUnit)

  const d = tz ? dayjs(startsAt).tz(tz) : dayjs(startsAt)
  const datePart = d.format("MMM D, YYYY")

  if (allDay || durationUnit === "day") {
    return datePart
  }

  return `${datePart} · ${d.format("h:mm A")}`
}

export const getBookableEventCardLocationKindLabel = (item: {
  kind?: string
  virtual?: boolean
}): string => {
  if (item.kind === "virtual" || item.virtual) return "Virtual"
  return "In-person"
}

export const groupEventCardDateLabel = (
  item: EventCardItem,
  practice?: { bookableEventTypes?: PracticeEventTypes }
): string => {
  const occurrence = nextUpcomingOccurrenceForEventCard(item, practice)
  if (!occurrence) return ""
  return formatBookableEventOccurrenceCardDate(occurrence, item)
}
